import DOMPurify from 'dompurify';

import { resolveServeDocumentSrc } from './appMessage';

const EMOJI_MAP: Record<string, string> = {
  white_check_mark: '✅',
  heavy_check_mark: '✔️',
  x: '❌',
  warning: '⚠️',
  no_entry: '⛔',
  rotating_light: '🚨',
  fire: '🔥',
  bell: '🔔',
  eyes: '👀',
  thumbsup: '👍',
  thumbsdown: '👎',
  tada: '🎉',
  clap: '👏',
  heart: '❤️',
  '100': '💯',
  star: '⭐',
  rocket: '🚀',
  information_source: 'ℹ️',
  memo: '📝',
  link: '🔗',
  mag: '🔍',
  calendar: '📅',
  hourglass: '⏳',
  computer: '💻',
  gear: '⚙️',
  wrench: '🔧',
  lock: '🔒',
  key: '🔑',
  arrow_right: '➡️',
  arrow_left: '⬅️',
  arrow_up: '⬆️',
  arrow_down: '⬇️',
  question: '❓',
  exclamation: '❗',
};

export const resolveEmoji = (source: string): string =>
  source.replace(
    /:([a-z0-9_+\-]+):/g,
    (match, code: string) => EMOJI_MAP[code] ?? match,
  );

export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const toCssFontSize = (value: string): string | null => {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)(px|em|rem|%|pt)$/i);
  if (!match) {
    return null;
  }
  const amount = match[1];
  const unit = match[2].toLowerCase();
  if (unit === 'pt') {
    return `${Math.round((Number(amount) * 96) / 72)}px`;
  }
  return `${amount}${unit}`;
};

const normalizeColorValue = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, '');

const isDefaultPasteColor = (value: string) =>
  [
    'rgb(0,0,0)',
    'rgba(0,0,0,1)',
    'rgba(0,0,0,1.0)',
    '#000',
    '#000000',
    'black',
    'windowtext',
  ].includes(normalizeColorValue(value));

const isDefaultPasteFontSize = (value: string) =>
  /^(10pt|11pt|12pt|13\.33px|14px|1em)$/i.test(value.trim());

const readStyleProperty = (style: string, property: string) =>
  style.match(new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`, 'i'))?.[1];

const readStyleFromTagAttrs = (attrs: string) => {
  const match = attrs.match(/style\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
  return match?.[1] ?? match?.[2] ?? '';
};

const flattenInsignificantHtmlSpans = (source: string): string => {
  let current = source;
  let previous = '';
  while (current !== previous) {
    previous = current;
    current = current.replace(
      /<span\b([^>]*)>([\s\S]*?)<\/span>/gi,
      (match, attrs: string, inner: string) => {
        const style = readStyleFromTagAttrs(attrs);
        if (!style.trim()) {
          return inner;
        }
        const color = readStyleProperty(style, 'color');
        const fontSize = readStyleProperty(style, 'font-size');
        const extra = style
          .split(';')
          .map((part) => part.trim())
          .filter(Boolean)
          .filter((part) => !/^(color|font-size)\s*:/i.test(part));
        if (extra.length > 0) {
          return match;
        }
        const colorIsDefault = !color || isDefaultPasteColor(color);
        const sizeIsDefault = !fontSize || isDefaultPasteFontSize(fontSize);
        return colorIsDefault && sizeIsDefault ? inner : match;
      },
    );
  }
  return current;
};

const stripMarkdownHardBreaks = (source: string): string =>
  source.replace(/\\(?=\r?\n|$)/g, '');

const renderInlineContent = (source: string): string => {
  const { tokenized, replacements } = tokenizeLinks(source);
  const normalizedInlineHtml = normalizeEscapedInlineHtml(tokenized);

  const withFormatting = normalizedInlineHtml
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/~~([^~]+)~~/g, '<s>$1</s>')
    .replace(/==([^=]+)==/g, '<mark>$1</mark>')
    .replace(
      /`([^`]+)`/g,
      '<code class="rounded bg-slate-200 px-1 dark:bg-slate-600">$1</code>',
    )
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(
      /@([\w][\w ]+)/g,
      '<span class="rounded bg-sky-100 px-1 font-medium text-sky-900 dark:bg-sky-900/50 dark:text-sky-200">@$1</span>',
    );

  return Array.from(replacements.entries()).reduce(
    (current, [token, replacement]) => current.replaceAll(token, replacement),
    withFormatting,
  );
};

const isTableSeparator = (line: string) =>
  /^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/.test(line);

const isTableRow = (line: string) => {
  const trimmed = line.trim();
  return trimmed.includes('|') && !trimmed.startsWith('>');
};

const splitTableCells = (line: string): string[] => {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let current = '';
  let escaped = false;

  for (const char of trimmed) {
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      continue;
    }
    if (char === '|') {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }

  cells.push(current.trim());
  return cells;
};

const getColumnAlignments = (separator: string) =>
  splitTableCells(separator).map((cell) => {
    const trimmed = cell.trim();
    if (trimmed.startsWith(':') && trimmed.endsWith(':')) {
      return 'center';
    }
    if (trimmed.endsWith(':')) {
      return 'right';
    }
    if (trimmed.startsWith(':')) {
      return 'left';
    }
    return undefined;
  });

const renderTableBlock = (rows: string[]): string => {
  const [headerRow, separatorRow, ...bodyRows] = rows;
  const alignments = getColumnAlignments(separatorRow);
  const headerCells = splitTableCells(headerRow)
    .map((cell, index) => {
      const align = alignments[index];
      return `<th class="border border-slate-300 bg-slate-100 px-3 py-2 font-semibold text-slate-800"${align ? ` style="text-align: ${align}"` : ''}>${renderInlineContent(cell)}</th>`;
    })
    .join('');

  const body = bodyRows
    .map((row) => {
      const cells = splitTableCells(row)
        .map((cell, index) => {
          const align = alignments[index];
          return `<td class="border border-slate-300 px-3 py-2 align-top"${align ? ` style="text-align: ${align}"` : ''}>${renderInlineContent(cell)}</td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  return `<div class="my-3 overflow-x-auto"><table class="min-w-full border-collapse text-sm text-slate-700"><thead><tr>${headerCells}</tr></thead><tbody>${body}</tbody></table></div>`;
};

const TABLE_CELL_TAGS = [
  'a',
  'b',
  'br',
  'code',
  'em',
  'font',
  'i',
  'img',
  'mark',
  's',
  'span',
  'strike',
  'strong',
  'u',
];

const TABLE_CELL_ATTRS = [
  'alt',
  'color',
  'colspan',
  'data-inline-image-alt',
  'data-inline-image-src',
  'href',
  'rel',
  'rowspan',
  'src',
  'style',
  'target',
];

const ALLOWED_STYLE_PROPS =
  /^(color|font-size|background-color|text-align|text-decoration|text-decoration-line)$/i;

const sanitizeStyleAttribute = (style: string) =>
  style
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .flatMap((part) => {
      const separator = part.indexOf(':');
      if (separator < 0) {
        return [];
      }
      const property = part.slice(0, separator).trim();
      const value = part.slice(separator + 1).trim();
      if (!ALLOWED_STYLE_PROPS.test(property) || /url\s*\(/i.test(value)) {
        return [];
      }
      return [`${property}: ${value}`];
    })
    .join('; ');

const withSanitizedStyles = <T>(run: () => T): T => {
  const hook = (
    _node: Element,
    data: { attrName: string; attrValue: string; keepAttr?: boolean },
  ) => {
    if (data.attrName !== 'style') {
      return;
    }
    data.attrValue = sanitizeStyleAttribute(data.attrValue);
    if (!data.attrValue) {
      data.keepAttr = false;
    }
  };
  DOMPurify.addHook('uponSanitizeAttribute', hook);
  try {
    return run();
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute');
  }
};

const sanitizeTableCellContent = (content: string) =>
  withSanitizedStyles(() =>
    DOMPurify.sanitize(content, {
      ALLOWED_TAGS: TABLE_CELL_TAGS,
      ALLOWED_ATTR: TABLE_CELL_ATTRS,
      ADD_DATA_URI_TAGS: ['img'],
    }),
  );

const getAllowedTableCellStyle = (value: string) => {
  const allowedParts: string[] = [];
  const textAlignMatch = value.match(
    /(?:^|;)\s*text-align\s*:\s*(left|right|center)\s*(?:;|$)/i,
  );
  const backgroundColorMatch = value.match(
    /(?:^|;)\s*background-color\s*:\s*(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-z]+)\s*(?:;|$)/i,
  );

  if (textAlignMatch) {
    allowedParts.push(`text-align: ${textAlignMatch[1].toLowerCase()}`);
  }
  if (backgroundColorMatch) {
    allowedParts.push(`background-color: ${backgroundColorMatch[1].trim()}`);
  }

  return allowedParts.join('; ');
};

const getTableCellContent = (cell: HTMLTableCellElement) => {
  const paragraphs = Array.from(cell.children).filter(
    (child): child is HTMLParagraphElement => child.tagName === 'P',
  );

  if (paragraphs.length > 0 && paragraphs.length === cell.children.length) {
    return paragraphs
      .map((paragraph) => sanitizeTableCellContent(paragraph.innerHTML.trim()))
      .filter(Boolean)
      .join('<br />');
  }

  return sanitizeTableCellContent(cell.innerHTML.trim());
};

const normalizeHtmlTableBlock = (tableHtml: string): string => {
  if (typeof DOMParser === 'undefined') {
    return `<div class="my-3 overflow-x-auto">${tableHtml}</div>`;
  }

  const parsed = new DOMParser().parseFromString(tableHtml, 'text/html');
  const table = parsed.querySelector('table');
  if (!table) {
    return tableHtml;
  }

  const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tr'));
  const normalizedRows = rows
    .map((row) => {
      const cells = Array.from(
        row.querySelectorAll<HTMLTableCellElement>('th, td'),
      )
        .map((cell) => {
          const tagName = cell.tagName === 'TH' ? 'th' : 'td';
          const align = (
            cell.getAttribute('align') ||
            cell.style.textAlign ||
            ''
          )
            .trim()
            .toLowerCase();
          const backgroundColor = cell.style.backgroundColor.trim();
          const style = getAllowedTableCellStyle(
            [
              align ? `text-align: ${align}` : '',
              backgroundColor ? `background-color: ${backgroundColor}` : '',
            ]
              .filter(Boolean)
              .join('; '),
          );
          const spanAttrs = [
            cell.colSpan > 1 ? ` colspan="${cell.colSpan}"` : '',
            cell.rowSpan > 1 ? ` rowspan="${cell.rowSpan}"` : '',
            style ? ` style="${style}"` : '',
          ].join('');
          const baseClass =
            tagName === 'th'
              ? 'border border-slate-300 bg-slate-100 px-3 py-2 font-semibold text-slate-800'
              : 'border border-slate-300 px-3 py-2 align-top';
          return `<${tagName} class="${baseClass}"${spanAttrs}>${getTableCellContent(cell)}</${tagName}>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  return `<div class="my-3 overflow-x-auto"><table class="min-w-full border-collapse text-sm text-slate-700"><tbody>${normalizedRows}</tbody></table></div>`;
};

const tokenizeHtmlTables = (
  source: string,
): { tokenized: string; replacements: Map<string, string> } => {
  const replacements = new Map<string, string>();
  let tableIndex = 0;

  const tokenized = source.replace(/<table\b[\s\S]*?<\/table>/gi, (match) => {
    const token = `%%ATGTABLE${tableIndex}%%`;
    replacements.set(token, normalizeHtmlTableBlock(match));
    tableIndex += 1;
    return token;
  });

  return { tokenized, replacements };
};

const tokenizeHtmlSpans = (
  source: string,
): { tokenized: string; replacements: Map<string, string> } => {
  const replacements = new Map<string, string>();
  let spanIndex = 0;

  const tokenized = source.replace(
    /<span\b[^>]*>[\s\S]*?<\/span>/gi,
    (match) => {
      if (!/\r|\n/.test(match)) {
        return match;
      }

      const token = `%%ATGSPAN${spanIndex}%%`;
      spanIndex += 1;
      replacements.set(
        token,
        match.replace(
          /<span\b([^>]*)>([\s\S]*?)<\/span>/i,
          (_innerMatch, attrs: string, inner: string) => {
            const style = sanitizeStyleAttribute(readStyleFromTagAttrs(attrs));
            const htmlInner = escapeHtml(inner).replace(/\r?\n/g, '<br />');
            return style
              ? `<span style="${style}">${htmlInner}</span>`
              : htmlInner;
          },
        ),
      );
      return token;
    },
  );

  return { tokenized, replacements };
};

const restorePlaceholders = (
  value: string,
  replacements: Map<string, string>,
) => {
  let current = value;
  replacements.forEach((html, token) => {
    current = current.replaceAll(token, html);
  });
  return current;
};

export const tokenizeLinks = (
  source: string,
): { tokenized: string; replacements: Map<string, string> } => {
  const replacements = new Map<string, string>();
  let linkIndex = 0;
  const normalizeHref = (value: string) =>
    /^(https?:\/\/|mailto:|\/|#)/i.test(value) ? value : `https://${value}`;

  const createToken = (html: string): string => {
    const token = `%%ATGLINK${linkIndex}%%`;
    replacements.set(token, html);
    linkIndex += 1;
    return token;
  };

  const markdownImaged = source.replace(
    /!\[([^\]]*)\]\(([^)\s]+)(?:\s+&quot;([^&]*)&quot;)?\)/g,
    (_, alt: string, url: string, fullUrl?: string) =>
      createToken(
        `<img class="my-2 max-h-72 max-w-full cursor-zoom-in rounded-xl border border-slate-200 object-contain" src="${resolveServeDocumentSrc(url)}" alt="${alt || 'image'}" data-inline-image-src="${resolveServeDocumentSrc(fullUrl || url)}" data-inline-image-alt="${alt || 'image'}" role="button" tabindex="0" />`,
      ),
  );

  const labeledLinked = markdownImaged.replace(
    /&lt;((?:https?:\/\/|\/)[^|&]+)\|([^&]+)&gt;/g,
    (_, url: string, label: string) =>
      createToken(
        `<a class="text-sky-700 underline dark:text-sky-300" href="${url}" target="_blank" rel="noreferrer">${label}</a>`,
      ),
  );

  const markdownLinked = labeledLinked.replace(
    /\[([^\]]+)\]\(((?:https?:\/\/|www\.|\/)[^)]+)\)/g,
    (_, label: string, url: string) =>
      createToken(
        `<a class="text-sky-700 underline dark:text-sky-300" href="${normalizeHref(url)}" target="_blank" rel="noreferrer">${label}</a>`,
      ),
  );

  const htmlLinked = markdownLinked.replace(
    /&lt;a\s+([^&]*href=(?:&quot;|&#039;)([^&]+)(?:&quot;|&#039;)[^&]*)&gt;([\s\S]+?)&lt;\/a&gt;/gi,
    (_match, _attrs: string, href: string, label: string) => {
      const url = href.replaceAll('&amp;', '&');
      if (!/^(https?:\/\/|www\.|\/|#|mailto:)/i.test(url)) {
        return label;
      }
      return createToken(
        `<a class="text-sky-700 underline dark:text-sky-300" href="${normalizeHref(url)}" target="_blank" rel="noreferrer">${label}</a>`,
      );
    },
  );

  const withRawLinks = htmlLinked.replace(
    /(^|[\s(])((?:https?:\/\/|www\.)[^\s<&]+)/g,
    (_match: string, prefix: string, url: string) =>
      `${prefix}${createToken(
        `<a class="text-sky-700 underline dark:text-sky-300" href="${normalizeHref(url)}" target="_blank" rel="noreferrer">${url}</a>`,
      )}`,
  );

  return { tokenized: withRawLinks, replacements };
};

function normalizeEscapedInlineHtml(source: string): string {
  return source
    .replace(
      /&lt;span\s+style=(?:&quot;|&#039;)([\s\S]*?)(?:&quot;|&#039;)&gt;([\s\S]+?)&lt;\/span&gt;/gi,
      (_match, style: string, text: string) => {
        const styleParts: string[] = [];
        const colorMatch = style.match(/(?:^|[;\s])color\s*:\s*([^;]+)/i);
        const fontSizeMatch = style.match(
          /(?:^|[;\s])font-size\s*:\s*([^;]+)/i,
        );

        const colorRaw = colorMatch?.[1].trim();
        const fontSizeRaw = fontSizeMatch?.[1].trim();
        const hasOtherStyle =
          /text-decoration|text-decoration-line|background/i.test(style);

        if (
          colorRaw &&
          !isDefaultPasteColor(colorRaw) &&
          /^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-z]+)$/i.test(
            colorRaw,
          )
        ) {
          styleParts.push(`color: ${colorRaw}`);
        }

        if (fontSizeRaw && !isDefaultPasteFontSize(fontSizeRaw)) {
          const safeFontSize = toCssFontSize(fontSizeRaw);
          if (safeFontSize) {
            styleParts.push(`font-size: ${safeFontSize}`);
          }
        }

        if (styleParts.length === 0) {
          return hasOtherStyle ? _match : text.replace(/\r?\n/g, '<br />');
        }

        return `<span style="${styleParts.join('; ')}">${text.replace(/\r?\n/g, '<br />')}</span>`;
      },
    )
    .replace(
      /&lt;span\s+style=(?:&quot;|&#039;)([\s\S]*?)(?:&quot;|&#039;)&gt;([\s\S]+?)&lt;\/span&gt;/gi,
      (_match, style: string, text: string) => {
        const colorMatch = style.match(/(?:^|[;\s])color\s*:\s*([^;]+)/i);
        if (!colorMatch) {
          return _match;
        }
        const safeColor = colorMatch[1].trim();
        if (
          !/^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-z]+)$/i.test(
            safeColor,
          )
        ) {
          return text;
        }
        return `<span style="color: ${safeColor}">${text}</span>`;
      },
    )
    .replace(
      /&lt;font\s+color=(?:&quot;|&#039;)?([^&"'>]+)(?:&quot;|&#039;)?&gt;([\s\S]+?)&lt;\/font&gt;/gi,
      (_match, color: string, text: string) => {
        const safeColor = color.trim();
        if (
          !/^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-z]+)$/i.test(
            safeColor,
          )
        ) {
          return text;
        }
        return `<span style="color: ${safeColor}">${text}</span>`;
      },
    )
    .replace(
      /&lt;span\s+style=(?:&quot;|&#039;)([^&]*(?:text-decoration\s*:\s*underline|text-decoration-line\s*:\s*underline)[^&]*)(?:&quot;|&#039;)&gt;([\s\S]+?)&lt;\/span&gt;/gi,
      '<u>$2</u>',
    )
    .replace(
      /&lt;span\s+style=(?:&quot;|&#039;)([^&]*(?:text-decoration\s*:\s*line-through|text-decoration-line\s*:\s*line-through)[^&]*)(?:&quot;|&#039;)&gt;([\s\S]+?)&lt;\/span&gt;/gi,
      '<s>$2</s>',
    )
    .replace(
      /&lt;span\s+style=(?:&quot;|&#039;)([^&]*(?:background|background-color)\s*:[^&]*)(?:&quot;|&#039;)&gt;([\s\S]+?)&lt;\/span&gt;/gi,
      '<mark>$2</mark>',
    )
    .replace(/&lt;u&gt;([\s\S]+?)&lt;\/u&gt;/g, '<u>$1</u>')
    .replace(
      /&lt;(?:s|strike)&gt;([\s\S]+?)&lt;\/(?:s|strike)&gt;/gi,
      '<s>$1</s>',
    )
    .replace(/&lt;mark&gt;([\s\S]+?)&lt;\/mark&gt;/gi, '<mark>$1</mark>');
}

function normalizeWordBulletLines(source: string): string {
  return source
    .split('\n')
    .map((line) => {
      const normalized = line
        .replace(/<span[^>]*>[·•]<\/span>/gi, '[[WORD_BULLET]]')
        .replace(/<span[^>]*>(?:\s|&nbsp;|&#160;|\u00a0)*<\/span>/gi, ' ')
        .trimStart();

      if (!normalized.startsWith('[[WORD_BULLET]]')) {
        return line;
      }

      const content = normalized
        .replace(/^(?:\[\[WORD_BULLET\]\]\s*)+/, '')
        .trim();

      return content ? `- ${content}` : '';
    })
    .join('\n');
}

const ORDERED_LIST_ITEM = /^\s*\d+\.\s+(.*)$/;

const looksLikeNewBlock = (line: string, htmlTokens: string[]) =>
  /^\s*#{1,6}\s/.test(line) ||
  /^\s*-\s+/.test(line) ||
  /^\s*>/.test(line) ||
  htmlTokens.some((token) => line.includes(token));

const collectOrderedList = (
  startIndex: number,
  lines: string[],
  htmlTokens: string[],
): { items: string[]; endIndex: number } | null => {
  const first = lines[startIndex].match(ORDERED_LIST_ITEM);
  if (!first) {
    return null;
  }

  const items: string[] = [];
  let current = first[1];
  let nextIndex = startIndex + 1;

  while (nextIndex < lines.length) {
    const nextLine = lines[nextIndex];
    const nextItem = nextLine.match(ORDERED_LIST_ITEM);
    if (nextItem) {
      items.push(current);
      current = nextItem[1];
      nextIndex += 1;
      continue;
    }

    if (!nextLine.trim()) {
      let peekIndex = nextIndex + 1;
      while (peekIndex < lines.length && !lines[peekIndex].trim()) {
        peekIndex += 1;
      }
      const peekLine = lines[peekIndex] ?? '';
      if (!peekLine || looksLikeNewBlock(peekLine, htmlTokens)) {
        break;
      }
      if (ORDERED_LIST_ITEM.test(peekLine)) {
        nextIndex = peekIndex;
        continue;
      }
      const restHasOrderedItem = lines
        .slice(peekIndex)
        .some((line) => ORDERED_LIST_ITEM.test(line));
      if (!restHasOrderedItem) {
        break;
      }
      current = `${current}\n${peekLine.trim()}`;
      nextIndex = peekIndex + 1;
      continue;
    }

    if (looksLikeNewBlock(nextLine, htmlTokens)) {
      break;
    }

    current = `${current}\n${nextLine.trim()}`;
    nextIndex += 1;
  }

  items.push(current);
  return { items, endIndex: nextIndex - 1 };
};

const renderListItems = (items: string[]) =>
  items
    .map(
      (item) =>
        `<li>${item
          .split('\n')
          .map((part) => renderInlineContent(part))
          .join('<br />')}</li>`,
    )
    .join('');

export const markdownToHtml = (source: string): string => {
  if (!source.trim()) {
    return '<span class="text-slate-500 dark:text-slate-400">No markdown text.</span>';
  }

  const prepared = stripMarkdownHardBreaks(
    flattenInsignificantHtmlSpans(
      normalizeWordBulletLines(resolveEmoji(source)),
    ),
  );
  const { tokenized: sourceWithTableTokens, replacements: tableReplacements } =
    tokenizeHtmlTables(prepared);
  const { tokenized: sourceWithHtmlTokens, replacements: spanReplacements } =
    tokenizeHtmlSpans(sourceWithTableTokens);
  const htmlReplacements = new Map([...tableReplacements, ...spanReplacements]);
  const htmlTokens = [...htmlReplacements.keys()];
  const escaped = escapeHtml(sourceWithHtmlTokens);
  const lines = escaped.split('\n');

  const blocks: string[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      continue;
    }

    if (htmlTokens.some((token) => trimmed.includes(token))) {
      blocks.push(restorePlaceholders(line, htmlReplacements));
      continue;
    }

    if (
      index + 1 < lines.length &&
      isTableRow(line) &&
      isTableSeparator(lines[index + 1])
    ) {
      const tableRows = [line, lines[index + 1]];
      let nextIndex = index + 2;
      while (
        nextIndex < lines.length &&
        lines[nextIndex].trim() &&
        isTableRow(lines[nextIndex])
      ) {
        tableRows.push(lines[nextIndex]);
        nextIndex += 1;
      }
      blocks.push(renderTableBlock(tableRows));
      index = nextIndex - 1;
      continue;
    }

    const orderedList = collectOrderedList(index, lines, htmlTokens);
    if (orderedList) {
      blocks.push(
        `<ol class="my-2 list-decimal pl-5">${renderListItems(orderedList.items)}</ol>`,
      );
      index = orderedList.endIndex;
      continue;
    }

    const bulletMatch = line.match(/^\s*-\s+(.*)$/);
    if (bulletMatch) {
      const items = [bulletMatch[1]];
      let nextIndex = index + 1;
      while (nextIndex < lines.length) {
        const nextMatch = lines[nextIndex].match(/^\s*-\s+(.*)$/);
        if (!nextMatch) {
          break;
        }
        items.push(nextMatch[1]);
        nextIndex += 1;
      }
      blocks.push(
        `<ul class="my-2 list-disc pl-5">${renderListItems(items)}</ul>`,
      );
      index = nextIndex - 1;
      continue;
    }

    blocks.push(`<p>${renderInlineContent(line)}</p>`);
  }

  const normalized = restorePlaceholders(
    blocks.join('').replace(/<p><\/p>/g, ''),
    htmlReplacements,
  );

  return withSanitizedStyles(() =>
    DOMPurify.sanitize(normalized, {
      ADD_TAGS: [
        'img',
        'u',
        's',
        'mark',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'div',
        'ol',
        'ul',
        'li',
        'span',
        'br',
        'p',
      ],
      ADD_ATTR: [
        'href',
        'target',
        'rel',
        'title',
        'src',
        'alt',
        'class',
        'colspan',
        'rowspan',
        'style',
        'role',
        'tabindex',
        'data-inline-image-src',
        'data-inline-image-alt',
      ],
      ADD_DATA_URI_TAGS: ['img'],
    }),
  );
};

export const blockStr = (obj: Record<string, unknown>, key: string): string => {
  const val = obj[key];
  return typeof val === 'string' ? val : '';
};

export const blockText = (block: Record<string, unknown>): string => {
  const textObj = block['text'];
  if (textObj && typeof textObj === 'object') {
    const inner = (textObj as Record<string, unknown>)['text'];
    return typeof inner === 'string' ? inner : '';
  }
  return '';
};

export const blockElements = (
  block: Record<string, unknown>,
): Array<Record<string, unknown>> => {
  const elements = block['elements'];
  return Array.isArray(elements)
    ? (elements as Array<Record<string, unknown>>)
    : [];
};

export const blockFields = (
  block: Record<string, unknown>,
): Array<Record<string, unknown>> => {
  const fields = block['fields'];
  return Array.isArray(fields)
    ? (fields as Array<Record<string, unknown>>)
    : [];
};
